import React from 'react';
import { Link } from 'react-router-dom';
import { Globe, Target, Users, Calendar, Clock, MapPin, Mail, Image as ImageIcon, Share2, ArrowUpRight, BookOpen } from 'lucide-react';
import HeroCarousel from '../../components/HeroCarousel';

// ARCHETYPE 1: The Living Archive — Historical split-pane, timeline nav, sepia tones
// Unique: Courier Prime monospace, sidebar info panel, Playfair Display headings,
// archival section names (Chronicle, Council, Archive, Film Reels)
const ArchiveTemplate = ({ association: a, activities, theme: t, onShare }) => {
  const C = {
    bg: t.bg || '#FDFBF7',
    primary: t.primary || '#1C1A17',
    secondary: t.accent || '#D4CBB3',
    accent: t.secondary || '#4A3E3D',
  };

  return (
    <div style={{ backgroundColor: C.bg, color: C.primary, fontFamily: "'Courier Prime', monospace" }} className="min-h-screen">
      {/* Nav */}
      <nav className="fixed top-0 left-0 right-0 z-50 border-b" style={{ backgroundColor: C.bg, borderColor: C.secondary }} data-testid="association-nav">
        <div className="max-w-7xl mx-auto px-6 flex items-center justify-between h-14">
          <div className="flex items-center gap-3">
            <img src={a.logo_url || a.image_url} alt="" className="w-10 h-10 rounded-full object-cover border" style={{ borderColor: C.accent }} />
            <span style={{ fontFamily: "'Playfair Display', serif", fontWeight: 700, color: C.primary }} className="text-lg">{a.short_name}</span>
          </div>
          <div className="hidden md:flex items-center gap-6 text-xs uppercase tracking-[0.15em]" style={{ color: C.accent }}>
            {['Chronicle','Heritage','Council','Events','Archive'].map(s => <a key={s} href={`#${s.toLowerCase()}`} className="hover:underline underline-offset-4">{s}</a>)}
          </div>
          <div className="flex items-center gap-3">
            <button onClick={onShare} className="p-2 hover:opacity-70" data-testid="share-btn"><Share2 className="w-4 h-4" /></button>
            <Link to="/" className="text-xs border px-3 py-1.5 hover:bg-black/5" style={{ borderColor: C.secondary, color: C.accent }} data-testid="cca-return-badge">CCA Home</Link>
          </div>
        </div>
      </nav>

      {/* Hero — Split pane with visible full-width banner */}
      <section id="chronicle" className="pt-14" data-testid="association-hero">
        <HeroCarousel
          images={a.hero_images?.length ? a.hero_images : []}
          overlay={`linear-gradient(to bottom, transparent 30%, ${C.bg}cc 85%, ${C.bg})`}
          className="h-[55vh]"
        >
          <div className="absolute bottom-6 left-6 sm:left-12">
            <span className="text-xs uppercase tracking-[0.3em]" style={{ color: C.accent }}>{a.region} Region</span>
            <h1 className="text-5xl sm:text-6xl lg:text-7xl font-bold" style={{ fontFamily: "'Playfair Display', serif", color: C.primary }} data-testid="association-name">{a.short_name}</h1>
          </div>
        </HeroCarousel>
        {/* Split pane below */}
        <div className="flex">
          <div className="hidden lg:block w-80 flex-shrink-0 border-r p-8 sticky top-14 h-[calc(100vh-3.5rem)]" style={{ borderColor: C.secondary, backgroundColor: `${C.secondary}30` }}>
            <div className="space-y-6">
              <div className="w-12 h-px" style={{ backgroundColor: C.accent }} />
              <div className="space-y-3 text-sm">
                <div><span className="text-xs uppercase tracking-widest block" style={{ color: C.accent }}>Region</span>{a.region}</div>
                {a.president_name && <div><span className="text-xs uppercase tracking-widest block" style={{ color: C.accent }}>President</span>{a.president_name}</div>}
                {a.origin && <div><span className="text-xs uppercase tracking-widest block" style={{ color: C.accent }}>Origin</span>{a.origin}</div>}
              </div>
            </div>
          </div>
          <div className="flex-1 p-8 sm:p-12 lg:p-16">
            <p className="text-xl italic mb-6" style={{ fontFamily: "'Playfair Display', serif", color: C.accent }}>{a.name}</p>
            {a.cultural_highlights && <p className="border-l-2 pl-4 max-w-xl leading-relaxed" style={{ borderColor: C.accent, color: `${C.primary}99` }} data-testid="cultural-highlights-banner">{a.cultural_highlights}</p>}
          </div>
        </div>
      </section>

      {/* About — The Chronicle */}
      <section className="py-24 border-t" style={{ borderColor: C.secondary }}>
        <div className="max-w-4xl mx-auto px-6">
          <h2 className="text-3xl mb-8" style={{ fontFamily: "'Playfair Display', serif" }}>The Chronicle</h2>
          <p className="text-lg leading-relaxed mb-10" style={{ color: `${C.primary}88` }} data-testid="association-description">{a.description}</p>
          <div className="grid md:grid-cols-2 gap-8">
            <div className="border p-6" style={{ borderColor: C.secondary }}>
              <Target className="w-5 h-5 mb-3" style={{ color: C.accent }} />
              <h3 className="text-xl mb-3" style={{ fontFamily: "'Playfair Display', serif" }}>Mission</h3>
              <p style={{ color: `${C.primary}77` }} data-testid="association-mission">{a.mission}</p>
            </div>
            <div className="border p-6" style={{ borderColor: C.secondary }}>
              <h3 className="text-sm uppercase tracking-widest mb-4" style={{ color: C.accent }}>Values</h3>
              <div className="flex flex-wrap gap-2" data-testid="association-values">
                {(a.values||[]).map((v,i) => <span key={i} className="border px-3 py-1 text-sm" style={{ borderColor: C.secondary }}>{v}</span>)}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Heritage */}
      <section id="heritage" className="py-24" style={{ backgroundColor: `${C.secondary}25` }}>
        <div className="max-w-4xl mx-auto px-6">
          <BookOpen className="w-6 h-6 mb-4" style={{ color: C.accent }} />
          <h2 className="text-3xl mb-6" style={{ fontFamily: "'Playfair Display', serif" }}>Cultural Heritage</h2>
          <p className="text-lg leading-relaxed mb-8" style={{ color: `${C.primary}77` }} data-testid="cultural-background">{a.cultural_background || a.cultural_highlights}</p>
          <div className="grid sm:grid-cols-3 gap-4">
            {['Attire','Dance','Cuisine'].map((item,i) => (
              <div key={i} className="border p-5" style={{ borderColor: C.secondary, backgroundColor: C.bg }}>
                <h4 className="font-bold mb-2" style={{ fontFamily: "'Playfair Display', serif" }}>Traditional {item}</h4>
                <p className="text-sm" style={{ color: `${C.primary}66` }}>
                  {i===0 ? 'Members wear garments reflecting heritage during ceremonies.'
                   : i===1 ? 'Cultural dances passed through generations.'
                   : 'Traditional dishes shared at community gatherings.'}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Leadership — The Council */}
      <section id="council" className="py-24 border-t" style={{ borderColor: C.secondary }}>
        <div className="max-w-5xl mx-auto px-6">
          <h2 className="text-3xl mb-8" style={{ fontFamily: "'Playfair Display', serif" }}>The Council</h2>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
            <div className="border p-6 text-center" style={{ borderColor: C.accent }}>
              <div className="w-20 h-20 rounded-full mx-auto mb-3 overflow-hidden border" style={{ borderColor: C.accent }}>
                {a.president_photo_url
                  ? <img src={a.president_photo_url} alt="" className="w-full h-full object-cover" />
                  : <div className="w-full h-full flex items-center justify-center" style={{ backgroundColor: `${C.secondary}60` }}>
                      <span className="text-2xl font-bold" style={{ color: C.accent }}>{(a.president_name||'?')[0]}</span>
                    </div>
                }
              </div>
              <h4 className="font-bold" style={{ fontFamily: "'Playfair Display', serif" }}>{a.president_name||'Vacant'}</h4>
              <p className="text-sm" style={{ color: C.accent }}>President</p>
            </div>
            {(a.leadership||[]).map((l,i) => (
              <div key={l.id||i} className="border p-6 text-center" style={{ borderColor: C.secondary }}>
                <div className="w-20 h-20 rounded-full mx-auto mb-3 overflow-hidden" style={{ backgroundColor: `${C.secondary}40` }}>
                  {l.photo_url
                    ? <img src={l.photo_url} alt="" className="w-full h-full object-cover" />
                    : <div className="w-full h-full flex items-center justify-center"><span className="text-2xl" style={{ color: C.accent }}>{(l.name||'?')[0]}</span></div>
                  }
                </div>
                <h4 className="font-bold">{l.name}</h4>
                <p className="text-sm" style={{ color: C.accent }}>{l.role}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Events */}
      <section id="events" className="py-24" style={{ backgroundColor: `${C.secondary}20` }}>
        <div className="max-w-4xl mx-auto px-6">
          <h2 className="text-3xl mb-8" style={{ fontFamily: "'Playfair Display', serif" }}>Events</h2>
          {activities.length > 0 ? (
            <div className="space-y-4">
              {activities.map(ev => (
                <div key={ev.id} className="border-b pb-4" style={{ borderColor: C.secondary }} data-testid={`event-${ev.id}`}>
                  <div className="flex gap-4">
                    <div className="text-center w-14">
                      <span className="text-xl font-bold block">{ev.date?.split('-')[2]}</span>
                      <span className="text-xs uppercase" style={{ color: C.accent }}>{ev.date && new Date(ev.date+'T00:00:00').toLocaleDateString('en-US',{month:'short'})}</span>
                    </div>
                    <div>
                      <h4 className="font-bold" style={{ fontFamily: "'Playfair Display', serif" }}>{ev.title}</h4>
                      <p className="text-sm mt-1" style={{ color: `${C.primary}66` }}>{ev.description}</p>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          ) : <p style={{ color: `${C.primary}44` }}>No events recorded yet.</p>}
        </div>
      </section>

      {/* Gallery — The Archive */}
      <section id="archive" className="py-24 border-t" style={{ borderColor: C.secondary }}>
        <div className="max-w-5xl mx-auto px-6">
          <h2 className="text-3xl mb-8" style={{ fontFamily: "'Playfair Display', serif" }}>The Archive</h2>
          {a.gallery_images?.length > 0 ? (
            <div className="columns-2 md:columns-3 gap-4 space-y-4">
              {a.gallery_images.map((img,i) => (
                <div key={i} className="break-inside-avoid" data-testid={`gallery-image-${i}`}>
                  <img src={img.url} alt="" className="w-full object-cover hover:opacity-80 transition-opacity" loading="lazy" />
                </div>
              ))}
            </div>
          ) : <p style={{ color: `${C.primary}44` }}>Archive growing — photos coming soon.</p>}
        </div>
      </section>

      {/* Videos — Film Reels */}
      <section className="py-24" style={{ backgroundColor: `${C.secondary}20` }}>
        <div className="max-w-4xl mx-auto px-6">
          <h2 className="text-3xl mb-8" style={{ fontFamily: "'Playfair Display', serif" }}>Film Reels</h2>
          {(a.youtube_videos||[]).length > 0 ? (
            <div className="grid md:grid-cols-2 gap-6">
              {a.youtube_videos.map((v,i) => (
                <div key={i} className="border" style={{ borderColor: C.secondary }} data-testid={`video-${i}`}>
                  <div className="aspect-video"><iframe src={`https://www.youtube.com/embed/${v.video_id}`} title={v.title||''} className="w-full h-full" allowFullScreen /></div>
                  {v.title && <div className="p-3" style={{ backgroundColor: C.bg }}><h4 className="font-bold text-sm">{v.title}</h4></div>}
                </div>
              ))}
            </div>
          ) : <p style={{ color: `${C.primary}44` }}>Film reels coming soon.</p>}
        </div>
      </section>

      {/* Footer */}
      <footer className="py-16 border-t text-center" style={{ borderColor: C.secondary, backgroundColor: C.primary, color: C.secondary }}>
        <img src={a.logo_url || a.image_url} alt="" className="w-14 h-14 rounded-full mx-auto mb-4 object-cover border" style={{ borderColor: C.accent }} />
        <h3 className="text-2xl mb-2" style={{ fontFamily: "'Playfair Display', serif" }}>{a.short_name}</h3>
        <p className="text-sm opacity-60 mb-6">{a.name}</p>
        <div className="flex justify-center gap-6 text-xs opacity-50">
          <Link to="/" className="hover:opacity-100 flex items-center gap-1">CCA Montreal <ArrowUpRight className="w-3 h-3" /></Link>
          <Link to="/associations" className="hover:opacity-100">Associations</Link>
          <Link to="/calendar" className="hover:opacity-100">Events</Link>
        </div>
      </footer>
    </div>
  );
};
export default ArchiveTemplate;
